import * as XLSX from "xlsx";
import { db } from "@/db/db";
import { Log } from "@/types/log.types";

export class LogRepository {
  async findAll(): Promise<Log[]> {
    const logs = await db.logs.toArray();
    return logs.map(log => ({
      ...log,
      createdAt: this.serializeDate(log.createdAt)
    }));
  }

  async findById(id: string): Promise<Log | undefined> {
    const log = await db.logs.get(id);
    if (log) {
      return {
        ...log,
        createdAt: this.serializeDate(log.createdAt)
      };
    }
    return undefined;
  }

  async findByOperation(operation: string): Promise<Log[]> {
    const logs = await db.logs.where("operation").equals(operation).toArray();
    return logs.map(log => ({
      ...log,
      createdAt: this.serializeDate(log.createdAt)
    }));
  }

  async findByResource(resource: string): Promise<Log[]> {
    const logs = await db.logs.where("resource").equals(resource).toArray();
    return logs.map(log => ({
      ...log,
      createdAt: this.serializeDate(log.createdAt)
    }));
  }

  /**
   * 将 Date 对象序列化为 ISO 字符串
   */
  private serializeDate(date: unknown): string {
    if (date instanceof Date) {
      return date.toISOString();
    }
    return String(date);
  }

  async create(log: Omit<Log, "id" | "createdAt">): Promise<Log> {
    const newLog: Log = {
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      ...log,
    };
    await db.logs.add(newLog);
    return newLog;
  }

  async delete(id: string): Promise<void> {
    await db.logs.delete(id);
  }

  async deleteAll(): Promise<void> {
    await db.logs.clear();
  }

  async exportLogs(format: "csv" | "json" | "excel"): Promise<Blob> {
    const logs = await this.findAll();

    switch (format) {
      case "csv":
        return this.exportAsCSV(logs);
      case "json":
        return this.exportAsJSON(logs);
      case "excel":
        return this.exportAsExcel(logs);
      default: {
        const formatStr = format as string;
        throw new Error(`不支持的导出格式: ${formatStr}`);
      }
    }
  }

  private exportAsCSV(logs: Log[]): Blob {
    const headers = ["id", "operation", "resource", "status", "details", "error", "createdAt"];
    const rows = logs.map((log) => [
      log.id,
      log.operation,
      log.resource,
      log.status,
      log.details || "",
      log.error || "",
      log.createdAt,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    return new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  }

  private exportAsJSON(logs: Log[]): Blob {
    const jsonContent = JSON.stringify(logs, null, 2);
    return new Blob([jsonContent], { type: "application/json;charset=utf-8;" });
  }

  private exportAsExcel(logs: Log[]): Blob {
    const workbook = XLSX.utils.book_new();
    const data = logs.map((log) => ({
      ID: log.id,
      操作类型: log.operation,
      资源类型: log.resource,
      状态: log.status,
      详情: log.details || "",
      错误: log.error || "",
      操作时间: log.createdAt,
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, "操作日志");

    const buffer = XLSX.write(workbook, { type: "array", bookType: "xlsx" }) as ArrayBuffer;
    return new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  }

  private generateId(): string {
    return `log_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }
}

export const logRepository = new LogRepository();
